import { useCallback } from 'react'
import { cn } from '@/lib/utils'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedChoiceProps<T extends string> {
  label: string
  value: T
  options: readonly SegmentedOption<T>[]
  onChange: (value: T) => void
}

interface SegmentProps<T extends string> {
  option: SegmentedOption<T>
  active: boolean
  onSelect: (value: T) => void
}

function Segment<T extends string>({ option, active, onSelect }: SegmentProps<T>) {
  const handleClick = useCallback(() => onSelect(option.value), [onSelect, option.value])
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={handleClick}
      className={cn(
        'h-7 flex-1 rounded-[5px] px-2 text-xs transition-colors',
        active ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {option.label}
    </button>
  )
}

/** A small one-of-N switch, used for the provider and billing choices. */
export function SegmentedChoice<T extends string>({ label, value, options, onChange }: SegmentedChoiceProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className="flex rounded-md bg-muted p-0.5">
      {options.map((option) => (
        <Segment key={option.value} option={option} active={option.value === value} onSelect={onChange} />
      ))}
    </div>
  )
}
